import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UploadCloud, FileCheck2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Migration() {
    const [type, setType] = useState("rooms");
    const [validation, setValidation] = useState(null);
    const [imported, setImported] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const [templates, setTemplates] = useState(null);

    React.useEffect(() => {
        api.get("/migration/templates").then((r) => setTemplates(r.data));
    }, []);

    const upload = async (action) => {
        const f = fileRef.current?.files?.[0];
        if (!f) {
            toast.error("Please choose a CSV file");
            return;
        }
        const fd = new FormData();
        fd.append("type", type);
        fd.append("file", f);
        setBusy(true);
        try {
            const { data } = await api.post(`/migration/${action}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (action === "validate") {
                setValidation(data);
                setImported(null);
                toast.success(`Validated ${data.row_count} rows`);
            } else {
                setImported(data);
                toast.success(`Imported ${data.inserted} rows`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                title="Migration Assistant"
                subtitle="Move rooms, guests and reservations from your existing system. Validate first, then import."
            />

            <Card className="mb-4">
                <CardContent className="py-4">
                    <Tabs value={type} onValueChange={setType}>
                        <TabsList>
                            <TabsTrigger value="rooms" data-testid="migration-tab-rooms">Rooms</TabsTrigger>
                            <TabsTrigger value="guests" data-testid="migration-tab-guests">Guests</TabsTrigger>
                            <TabsTrigger value="reservations" data-testid="migration-tab-reservations">Reservations</TabsTrigger>
                        </TabsList>
                        <TabsContent value={type} className="mt-4 space-y-3">
                            <div className="rounded-lg border-2 border-dashed border-border p-6 text-center bg-secondary/40">
                                <UploadCloud className="w-6 h-6 mx-auto text-muted-foreground" />
                                <div className="text-sm mt-2">Drop or choose a CSV file to import {type}.</div>
                                <input ref={fileRef} type="file" accept=".csv" className="block mx-auto mt-3 text-sm" data-testid="migration-csv-upload" />
                                <div className="flex justify-center gap-2 mt-3">
                                    <Button variant="outline" onClick={() => upload("validate")} disabled={busy} data-testid="migration-validate">
                                        <FileCheck2 className="w-4 h-4 mr-1" /> Validate
                                    </Button>
                                    <Button onClick={() => upload("import")} disabled={busy} data-testid="migration-import">
                                        Import
                                    </Button>
                                </div>
                            </div>
                            {templates && (
                                <div className="text-xs text-muted-foreground">
                                    <div className="font-medium mb-1">Sample CSV ({type}):</div>
                                    <pre className="bg-secondary rounded p-2 overflow-x-auto whitespace-pre text-[11px]">{templates[type]}</pre>
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>

            {validation && (
                <Card className="mb-4" data-testid="migration-validation">
                    <CardHeader><CardTitle className="text-base">Validation · {validation.row_count} rows</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-xs text-muted-foreground mb-2">Required columns: {validation.required_columns.join(", ")}</div>
                        {validation.errors.length === 0 ? (
                            <div className="text-sm text-[hsl(var(--success))]">All rows pass basic checks.</div>
                        ) : (
                            <ul className="text-sm space-y-1">
                                {validation.errors.slice(0, 10).map((e, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                        <AlertCircle className="w-3.5 h-3.5 text-[hsl(var(--warning))]" /> Row {e.row}: missing {e.missing.join(", ")}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            )}

            {imported && (
                <Card data-testid="migration-result">
                    <CardContent className="py-4">
                        <div className="text-sm">
                            Imported <span className="font-semibold">{imported.inserted}</span> {imported.type} rows.{" "}
                            {imported.skipped > 0 && <span className="text-muted-foreground">Skipped {imported.skipped}.</span>}
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    );
}
